import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container, Typography, TextField, Box, Button, Grid, Paper,
  CircularProgress, Alert, MenuItem, Select, FormControl, InputLabel,
  IconButton, Chip, Tooltip, FormHelperText, List, ListItem,
  ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  AddCircleOutline, RemoveCircleOutline, Hotel,
  ArrowBack, AttachFile, Image as ImageIcon, PictureAsPdf, Close,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { format, addDays, eachDayOfInterval, isValid } from 'date-fns';
import {
  Booking, DayBooking, RoomBooking, RoomType,
  ROOM_TYPES, ROOM_INVENTORY,
  createGuest, getGuestById, updateGuest, getAvailableRooms,
  uploadDocuments, getDocuments, BookingDocument,
} from '../services/api';
import {
  AMOUNT_OVERRIDE_REASON_LABEL,
  AMOUNT_OVERRIDE_REASON_OPTIONS,
  getChildRateFactor,
} from '../constants/pricing';
import {
  BOOKING_STATUS,
  BOOKING_STATUS_LABEL,
  BOOKING_STATUS_OPTIONS,
  BOOKING_TYPE,
  BOOKING_TYPE_LABEL,
  BOOKING_TYPE_OPTIONS,
  BookingType,
} from '../constants/booking';
import {
  GUEST_FORM_DATE_FORMAT,
  GUEST_FORM_FIELD_LABEL,
  GUEST_FORM_FILE_UPLOAD,
  GUEST_FORM_MESSAGE,
  GUEST_FORM_REFUND_STATUS,
  GUEST_FORM_ROOM,
  GUEST_FORM_STAFF_OPTIONS,
  GUEST_FORM_TEST_ID,
  GUEST_FORM_TEXT,
  GUEST_FORM_VALIDATION_LIMIT,
} from '../constants/guestForm';
import { APP_ROUTES } from '../constants/routes';
import { TOAST_SEVERITY } from '../constants/toast';
import { useToastActions } from '../store/toastHooks';

interface GuestFormProps { isEdit?: boolean; }

const CHILD_RATE_FACTOR = getChildRateFactor();

/** Returns today's local date in the ISO date format used by date inputs. */
const todayDateString = () => format(new Date(), GUEST_FORM_DATE_FORMAT.ISO_DATE);

/** Derives a valid booking type from saved data or defaults from the check-in date. */
const deriveBookingType = (checkinDate?: string, requestedType?: string | null): BookingType =>
  BOOKING_TYPE_OPTIONS.includes(requestedType as BookingType)
    ? requestedType as BookingType
    : checkinDate === todayDateString() ? BOOKING_TYPE.WALK_IN : BOOKING_TYPE.ADVANCE;

/** Calculates the booking total from room adult/child counts and per-day driver charge. */
const calculateTotalAmount = (dayBookings: DayBooking[], driverCount: number) =>
  dayBookings.reduce((total, day) => {
    const roomTotal = day.rooms.reduce((dayTotal, room) => {
      const adults = Number(room.NO_ADULTS_IN_ROOM) || 0;
      const children = Number(room.NO_CHILDREN_IN_ROOM) || 0;
      const amount = Number(room.AMOUNT_PER_PERSON) || 0;
      return dayTotal + (adults * amount) + (children * amount * CHILD_RATE_FACTOR);
    }, 0);
    return total + roomTotal + ((Number(day.DRIVER_AMOUNT_PER_DAY) || 0) * (Number(driverCount) || 0));
  }, 0);

/** Returns a booking copy with TOTAL_AMOUNT synchronized to the calculated amount. */
const withCalculatedTotal = (booking: Booking): Booking => ({
  ...booking,
  TOTAL_AMOUNT: calculateTotalAmount(booking.dayBookings || [], Number(booking.NO_DRIVERS) || 0),
});

/** Extracts the most useful API error text for toast display. */
const apiErrorToastMessage = (err: any, fallback: string): string => {
  const message = err?.response?.data?.message || fallback;
  const detail = err?.response?.data?.detail;
  return detail ? `${message}: ${detail}` : message;
};

/** Creates a default room allocation row for a new day booking. */
const emptyRoom = (): RoomBooking => ({
  ROOM_TYPE: GUEST_FORM_ROOM.DEFAULT_ROOM_TYPE,
  ROOM_NUMBER: '',
  NO_OF_ROOMS: 1,
  NO_ADULTS_IN_ROOM: 1,
  NO_CHILDREN_IN_ROOM: 0,
  AMOUNT_PER_PERSON: 0,
  CUSTOM_ROOM_TYPE: '',
});

/** Creates the default booking state used when opening a new guest form. */
const defaultBooking = (): Booking => ({
  GUEST_NAME: '',
  PHONE: '',
  EMAIL: '',
  ADDRESS: '',
  CHECKIN_DATE: '',
  CHECKOUT_DATE: '',
  BOOKING_TYPE: BOOKING_TYPE.ADVANCE,
  NO_ADULTS: 1,
  NO_CHILDREN: 0,
  NO_DRIVERS: 0,
  ADVANCE_AMOUNT: 0,
  TOTAL_AMOUNT: 0,
  BOOKED_BY: '',
  REMARKS: '',
  STATUS: BOOKING_STATUS.ACTIVE,
  CANCELLATION_REASON: '',
  IS_REFUNDED: null,
  REFUNDED_AMOUNT: 0,
  dayBookings: [],
});

const GuestForm: React.FC<GuestFormProps> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToastActions();
  const [formData, setFormData] = useState<Booking>(defaultBooking());
  const [availableRooms, setAvailableRooms] = useState<Record<string, Record<string, string[]>>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingDocs, setExistingDocs] = useState<BookingDocument[]>([]);
  const [amountConfirm, setAmountConfirm] = useState<{ systemAmount: number; enteredAmount: number } | null>(null);
  const [amountOverrideReason, setAmountOverrideReason] = useState('');
  const [amountOverrideNote, setAmountOverrideNote] = useState('');
  const [amountOverrideError, setAmountOverrideError] = useState('');
  const [amountOverrideNoteError, setAmountOverrideNoteError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const systemCalculatedTotal = useMemo(
    () => calculateTotalAmount(formData.dayBookings || [], Number(formData.NO_DRIVERS) || 0),
    [formData.NO_DRIVERS, formData.dayBookings]
  );
  const enteredTotal = Number(formData.TOTAL_AMOUNT) || 0;
  const totalDifference = enteredTotal - systemCalculatedTotal;
  const formatCurrency = useCallback((amount: number) => `₹${amount.toFixed(2)}`, []);
  const formatSignedCurrency = useCallback((amount: number) => `${amount >= 0 ? '+' : '-'}₹${Math.abs(amount).toFixed(2)}`, []);

  // Load booking for edit
  useEffect(() => {
    if (isEdit && id) {
      const fmt = (d: string) => { if (!d) return ''; const dt = new Date(d); return isValid(dt) ? format(dt, GUEST_FORM_DATE_FORMAT.ISO_DATE) : ''; };
      getGuestById(parseInt(id))
        .then(data => {
          const normalized = {
            ...data,
            CHECKIN_DATE: fmt(data.CHECKIN_DATE),
            CHECKOUT_DATE: fmt(data.CHECKOUT_DATE),
            BOOKING_TYPE: deriveBookingType(fmt(data.CHECKIN_DATE), data.BOOKING_TYPE),
            dayBookings: (data.dayBookings || []).map(day => {
              const dayHasOccupancy = day.rooms.some(room =>
                (Number(room.NO_ADULTS_IN_ROOM) || 0) > 0 ||
                (Number(room.NO_CHILDREN_IN_ROOM) || 0) > 0
              );
              return {
                ...day,
                STAY_DATE: fmt(day.STAY_DATE),
                DRIVER_AMOUNT_PER_DAY: Number(day.DRIVER_AMOUNT_PER_DAY) || 0,
                rooms: day.rooms.map((room, roomIdx) => ({
                  ...room,
                  NO_ADULTS_IN_ROOM: Number(room.NO_ADULTS_IN_ROOM) || (!dayHasOccupancy && roomIdx === 0 ? Number(data.NO_ADULTS) || 0 : 0),
                  NO_CHILDREN_IN_ROOM: Number(room.NO_CHILDREN_IN_ROOM) || (!dayHasOccupancy && roomIdx === 0 ? Number(data.NO_CHILDREN) || 0 : 0),
                })),
              };
            }),
          } as Booking;
          setFormData(normalized);
          setLoading(false);
        })
        .catch(() => { showToast(GUEST_FORM_MESSAGE.LOAD_BOOKING_FAILED, TOAST_SEVERITY.ERROR); setLoading(false); });
      // Load existing documents
      getDocuments(parseInt(id)).then(setExistingDocs).catch(() => {});
    }
  }, [isEdit, id, showToast]);

  // When checkin/checkout change, rebuild dayBookings skeleton
  const rebuildDayBookings = useCallback((checkin: string, checkout: string, existing: DayBooking[]) => {
    if (!checkin || !checkout) return [];
    const c = new Date(checkin), co = new Date(checkout);
    if (!isValid(c) || !isValid(co) || co < c) return [];
    const days = co.getTime() === c.getTime()
      ? [c]
      : eachDayOfInterval({ start: c, end: addDays(co, -1) });
    return days.map(d => {
      const dateStr = format(d, GUEST_FORM_DATE_FORMAT.ISO_DATE);
      const found = existing.find(e => e.STAY_DATE.split('T')[0] === dateStr);
      return found ? { ...found, DRIVER_AMOUNT_PER_DAY: Number(found.DRIVER_AMOUNT_PER_DAY) || 0 } : { STAY_DATE: dateStr, DRIVER_AMOUNT_PER_DAY: 0, rooms: [emptyRoom()] };
    });
  }, []);

  // Fetch available rooms for a specific date
  const fetchAvailableForDate = useCallback(async (date: string) => {
    try {
      const data = await getAvailableRooms(date, isEdit && id ? parseInt(id) : undefined);
      setAvailableRooms(prev => prev[date] ? prev : { ...prev, [date]: data.available });
    } catch {
      // silent
    }
  }, [isEdit, id]);

  /** Updates top-level booking fields and recalculates dependent booking totals when needed. */
  const handleTopField = useCallback((field: keyof Booking, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'CHECKIN_DATE' || field === 'CHECKOUT_DATE') {
        const c = field === 'CHECKIN_DATE' ? value : prev.CHECKIN_DATE;
        const co = field === 'CHECKOUT_DATE' ? value : prev.CHECKOUT_DATE;
        if (field === 'CHECKIN_DATE') updated.BOOKING_TYPE = deriveBookingType(c);
        updated.dayBookings = rebuildDayBookings(c, co, prev.dayBookings || []);
        return withCalculatedTotal(updated);
      }
      if (field === 'NO_DRIVERS') {
        if ((Number(value) || 0) <= 0) {
          updated.dayBookings = (prev.dayBookings || []).map(day => ({
            ...day,
            DRIVER_AMOUNT_PER_DAY: 0,
          }));
        }
        return withCalculatedTotal(updated);
      }
      return updated;
    });
    setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  }, [rebuildDayBookings]);

  /** Adds a new room allocation row to a specific stay date. */
  const addRoomToDay = useCallback((dayIdx: number) => {
    setFormData(prev => {
      const dayBookings = [...(prev.dayBookings || [])];
      dayBookings[dayIdx] = {
        ...dayBookings[dayIdx],
        rooms: [...dayBookings[dayIdx].rooms, emptyRoom()],
      };
      return withCalculatedTotal({ ...prev, dayBookings });
    });
  }, []);

  /** Updates a day-level booking field, such as driver amount, and recalculates totals. */
  const updateDayBooking = useCallback((dayIdx: number, field: keyof DayBooking, value: any) => {
    setFormData(prev => {
      const dayBookings = [...(prev.dayBookings || [])];
      dayBookings[dayIdx] = { ...dayBookings[dayIdx], [field]: value };
      return withCalculatedTotal({ ...prev, dayBookings });
    });
  }, []);

  /** Removes a room allocation row from a stay date and recalculates totals. */
  const removeRoomFromDay = useCallback((dayIdx: number, roomIdx: number) => {
    setFormData(prev => {
      const dayBookings = [...(prev.dayBookings || [])];
      const rooms = dayBookings[dayIdx].rooms.filter((_, i) => i !== roomIdx);
      dayBookings[dayIdx] = { ...dayBookings[dayIdx], rooms };
      return withCalculatedTotal({ ...prev, dayBookings });
    });
  }, []);

  /** Updates one room allocation field and keeps selected-room counts synchronized. */
  const updateRoom = useCallback((dayIdx: number, roomIdx: number, field: keyof RoomBooking, value: any) => {
    const stayDate = formData.dayBookings?.[dayIdx]?.STAY_DATE || '';
    setFormData(prev => {
      const dayBookings = [...(prev.dayBookings || [])];
      const rooms = [...dayBookings[dayIdx].rooms];
      rooms[roomIdx] = { ...rooms[roomIdx], [field]: value };
      // clear room number when type changes
      if (field === 'ROOM_TYPE') rooms[roomIdx].ROOM_NUMBER = '';
      // auto-sync NO_OF_ROOMS with count of selected room numbers
      if (field === 'ROOM_NUMBER') {
        const count = String(value).split(',').filter(Boolean).length;
        rooms[roomIdx].NO_OF_ROOMS = Math.max(1, count);
      }
      dayBookings[dayIdx] = { ...dayBookings[dayIdx], rooms };
      return withCalculatedTotal({ ...prev, dayBookings });
    });
    if (stayDate) fetchAvailableForDate(stayDate);
  }, [fetchAvailableForDate, formData.dayBookings]);

  /** Validates booking, cancellation, amount, and room member allocation rules before saving. */
  const validate = useCallback((): boolean => {
    const e: Record<string, string> = {};
    if (!formData.GUEST_NAME.trim()) e.GUEST_NAME = GUEST_FORM_MESSAGE.NAME_REQUIRED;
    else if (formData.GUEST_NAME.trim().length > GUEST_FORM_VALIDATION_LIMIT.NAME_MAX_LENGTH) e.GUEST_NAME = GUEST_FORM_MESSAGE.NAME_TOO_LONG;
    if (!formData.PHONE.trim()) e.PHONE = GUEST_FORM_MESSAGE.PHONE_REQUIRED;
    else if (formData.PHONE.trim().length > GUEST_FORM_VALIDATION_LIMIT.PHONE_MAX_LENGTH) e.PHONE = GUEST_FORM_MESSAGE.PHONE_TOO_LONG;
    else if (!/^[0-9+\-\s()]{7,20}$/.test(formData.PHONE.trim())) e.PHONE = GUEST_FORM_MESSAGE.PHONE_INVALID;
    if (formData.EMAIL && formData.EMAIL.trim().length > GUEST_FORM_VALIDATION_LIMIT.EMAIL_MAX_LENGTH) e.EMAIL = GUEST_FORM_MESSAGE.EMAIL_TOO_LONG;
    else if (formData.EMAIL && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.EMAIL)) e.EMAIL = GUEST_FORM_MESSAGE.EMAIL_INVALID;
    if (!formData.CHECKIN_DATE) e.CHECKIN_DATE = GUEST_FORM_MESSAGE.CHECKIN_REQUIRED;
    if (!formData.CHECKOUT_DATE) e.CHECKOUT_DATE = GUEST_FORM_MESSAGE.CHECKOUT_REQUIRED;
    if (!isEdit && formData.CHECKIN_DATE && formData.CHECKIN_DATE < todayDateString()) e.CHECKIN_DATE = GUEST_FORM_MESSAGE.CHECKIN_IN_PAST;
    if (formData.CHECKIN_DATE && formData.CHECKOUT_DATE && formData.CHECKOUT_DATE < formData.CHECKIN_DATE)
      e.CHECKOUT_DATE = GUEST_FORM_MESSAGE.CHECKOUT_BEFORE_CHECKIN;
    if (!formData.dayBookings?.length) e.dayBookings = GUEST_FORM_MESSAGE.DAY_BOOKING_REQUIRED;
    if (isEdit && !formData.STATUS) e.STATUS = GUEST_FORM_MESSAGE.STATUS_REQUIRED;
    if (!formData.NO_ADULTS || formData.NO_ADULTS < 1) e.NO_ADULTS = GUEST_FORM_MESSAGE.ADULT_REQUIRED;
    if (formData.NO_ADULTS > GUEST_FORM_VALIDATION_LIMIT.ADULT_MAX_COUNT) e.NO_ADULTS = GUEST_FORM_MESSAGE.GUEST_COUNT_TOO_LARGE;
    if (formData.NO_CHILDREN < 0) e.NO_CHILDREN = GUEST_FORM_MESSAGE.CHILDREN_NEGATIVE;
    if (formData.NO_CHILDREN > GUEST_FORM_VALIDATION_LIMIT.CHILD_MAX_COUNT) e.NO_CHILDREN = GUEST_FORM_MESSAGE.GUEST_COUNT_TOO_LARGE;
    if (formData.NO_DRIVERS < 0) e.NO_DRIVERS = GUEST_FORM_MESSAGE.DRIVERS_NEGATIVE;
    if (formData.NO_DRIVERS > GUEST_FORM_VALIDATION_LIMIT.DRIVER_MAX_COUNT) e.NO_DRIVERS = GUEST_FORM_MESSAGE.GUEST_COUNT_TOO_LARGE;
    if (formData.ADVANCE_AMOUNT < 0) e.ADVANCE_AMOUNT = GUEST_FORM_MESSAGE.ADVANCE_AMOUNT_NEGATIVE;
    if (formData.ADVANCE_AMOUNT > GUEST_FORM_VALIDATION_LIMIT.MAX_AMOUNT) e.ADVANCE_AMOUNT = GUEST_FORM_MESSAGE.AMOUNT_TOO_LARGE;
    const rawTotalAmount = formData.TOTAL_AMOUNT as unknown;
    if (rawTotalAmount === undefined || rawTotalAmount === null || rawTotalAmount === '') {
      e.TOTAL_AMOUNT = GUEST_FORM_MESSAGE.TOTAL_AMOUNT_REQUIRED;
    } else if (!Number.isFinite(Number(rawTotalAmount)) || Number(rawTotalAmount) < 0) {
      e.TOTAL_AMOUNT = GUEST_FORM_MESSAGE.TOTAL_AMOUNT_NEGATIVE;
    }
    if (Number(rawTotalAmount) > GUEST_FORM_VALIDATION_LIMIT.MAX_AMOUNT) e.TOTAL_AMOUNT = GUEST_FORM_MESSAGE.AMOUNT_TOO_LARGE;
    if (Number(formData.ADVANCE_AMOUNT) > Number(formData.TOTAL_AMOUNT)) e.ADVANCE_AMOUNT = GUEST_FORM_MESSAGE.ADVANCE_EXCEEDS_TOTAL;
    if (!formData.BOOKED_BY?.trim()) e.BOOKED_BY = GUEST_FORM_MESSAGE.BOOKED_BY_REQUIRED;
    else if (formData.BOOKED_BY.trim().length > GUEST_FORM_VALIDATION_LIMIT.BOOKED_BY_MAX_LENGTH) e.BOOKED_BY = GUEST_FORM_MESSAGE.BOOKED_BY_TOO_LONG;
    if (String(formData.ADDRESS || '').length > GUEST_FORM_VALIDATION_LIMIT.TEXT_MAX_LENGTH) e.ADDRESS = GUEST_FORM_MESSAGE.TEXT_TOO_LONG;
    if (String(formData.REMARKS || '').length > GUEST_FORM_VALIDATION_LIMIT.TEXT_MAX_LENGTH) e.REMARKS = GUEST_FORM_MESSAGE.TEXT_TOO_LONG;
    if (String(formData.CANCELLATION_REASON || '').length > GUEST_FORM_VALIDATION_LIMIT.TEXT_MAX_LENGTH) e.CANCELLATION_REASON = GUEST_FORM_MESSAGE.TEXT_TOO_LONG;
    if (String(formData.AMOUNT_OVERRIDE_NOTE || '').length > GUEST_FORM_VALIDATION_LIMIT.NOTE_MAX_LENGTH)
      e.AMOUNT_OVERRIDE_NOTE = GUEST_FORM_MESSAGE.TEXT_TOO_LONG;
    if (isEdit && formData.STATUS === BOOKING_STATUS.CANCELLED) {
      if (!formData.CANCELLATION_REASON?.trim()) e.CANCELLATION_REASON = GUEST_FORM_MESSAGE.CANCELLATION_REASON_REQUIRED;
      if (!formData.IS_REFUNDED) e.IS_REFUNDED = GUEST_FORM_MESSAGE.REFUND_SELECTION_REQUIRED;
      if (formData.IS_REFUNDED === GUEST_FORM_REFUND_STATUS.YES && (!formData.REFUNDED_AMOUNT || formData.REFUNDED_AMOUNT <= 0))
        e.REFUNDED_AMOUNT = GUEST_FORM_MESSAGE.REFUNDED_AMOUNT_REQUIRED;
      if (formData.IS_REFUNDED === GUEST_FORM_REFUND_STATUS.YES && Number(formData.REFUNDED_AMOUNT || 0) > Number(formData.ADVANCE_AMOUNT || 0))
        e.REFUNDED_AMOUNT = GUEST_FORM_MESSAGE.REFUNDED_AMOUNT_EXCEEDS_ADVANCE;
    }
    for (const day of (formData.dayBookings || [])) {
      const selectedRoomsForDay = new Set<string>();
      for (const room of day.rooms) {
        if (!room.ROOM_NUMBER) e[`room_${day.STAY_DATE}`] = GUEST_FORM_MESSAGE.ROOM_NUMBER_REQUIRED(day.STAY_DATE);
        if (room.ROOM_TYPE === GUEST_FORM_ROOM.OTHER_ROOM_TYPE && !room.CUSTOM_ROOM_TYPE?.trim())
          e[`custom_${day.STAY_DATE}`] = GUEST_FORM_MESSAGE.CUSTOM_ROOM_TYPE_REQUIRED(day.STAY_DATE);
        if (String(room.CUSTOM_ROOM_TYPE || '').length > GUEST_FORM_VALIDATION_LIMIT.TEXT_MAX_LENGTH)
          e[`custom_${day.STAY_DATE}`] = GUEST_FORM_MESSAGE.TEXT_TOO_LONG;
        if (String(room.ROOM_NUMBER || '').length > GUEST_FORM_VALIDATION_LIMIT.TEXT_MAX_LENGTH)
          e[`room_${day.STAY_DATE}`] = GUEST_FORM_MESSAGE.TEXT_TOO_LONG;
        const selectedRooms = String(room.ROOM_NUMBER || '').split(',').map(value => value.trim()).filter(Boolean);
        if (selectedRooms.length !== Number(room.NO_OF_ROOMS || 0)) e[`room_count_${day.STAY_DATE}`] = GUEST_FORM_MESSAGE.ROOM_COUNT_MISMATCH(day.STAY_DATE);
        if (room.ROOM_TYPE !== GUEST_FORM_ROOM.OTHER_ROOM_TYPE && selectedRooms.some(roomNumber => !(ROOM_INVENTORY[room.ROOM_TYPE] || []).includes(roomNumber)))
          e[`room_inventory_${day.STAY_DATE}`] = GUEST_FORM_MESSAGE.ROOM_NOT_IN_INVENTORY(day.STAY_DATE);
        for (const roomNumber of selectedRooms) {
          if (selectedRoomsForDay.has(roomNumber)) e[`duplicate_room_${day.STAY_DATE}`] = GUEST_FORM_MESSAGE.DUPLICATE_ROOM(day.STAY_DATE);
          selectedRoomsForDay.add(roomNumber);
        }
        if (Number(room.AMOUNT_PER_PERSON || 0) > GUEST_FORM_VALIDATION_LIMIT.MAX_AMOUNT) e[`amount_${day.STAY_DATE}`] = GUEST_FORM_MESSAGE.AMOUNT_TOO_LARGE;
      }
    }

    for (const day of (formData.dayBookings || [])) {
      const adultsForDay = day.rooms.reduce((sum, room) => sum + (Number(room.NO_ADULTS_IN_ROOM) || 0), 0);
      const childrenForDay = day.rooms.reduce((sum, room) => sum + (Number(room.NO_CHILDREN_IN_ROOM) || 0), 0);
      if (adultsForDay !== Number(formData.NO_ADULTS || 0) || childrenForDay !== Number(formData.NO_CHILDREN || 0)) {
        e[`members_${day.STAY_DATE}`] = GUEST_FORM_MESSAGE.ROOM_MEMBER_TOTAL(day.STAY_DATE, Number(formData.NO_ADULTS), Number(formData.NO_CHILDREN), adultsForDay, childrenForDay);
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData, isEdit]);

  /** Persists the booking and optional amount override details, then uploads selected documents. */
  const saveBooking = useCallback(async (amountOverride?: {
    reason: string;
    note?: string;
    systemAmount: number;
  }) => {
    setSaving(true);
    try {
      const bookingPayload: Booking = amountOverride
        ? {
            ...formData,
            AMOUNT_OVERRIDE_REASON: amountOverride.reason,
            AMOUNT_OVERRIDE_NOTE: amountOverride.note?.trim() || undefined,
            SYSTEM_CALCULATED_TOTAL: amountOverride.systemAmount,
          }
        : formData;

      if (isEdit && id) {
        await updateGuest(parseInt(id), bookingPayload);
        if (pendingFiles.length > 0) {
          try {
            await uploadDocuments(parseInt(id), pendingFiles);
          } catch (uploadErr: any) {
            const msg = uploadErr?.response?.data?.message || GUEST_FORM_MESSAGE.BOOKING_SAVED_UPLOAD_FAILED;
            showToast(msg, TOAST_SEVERITY.ERROR);
            setSaving(false);
            return;
          }
        }
        showToast(GUEST_FORM_MESSAGE.BOOKING_UPDATED, TOAST_SEVERITY.SUCCESS);
      } else {
        const { id: newId } = await createGuest(bookingPayload);
        if (pendingFiles.length > 0) {
          try {
            await uploadDocuments(newId, pendingFiles);
          } catch (uploadErr: any) {
            const msg = uploadErr?.response?.data?.message || GUEST_FORM_MESSAGE.BOOKING_CREATED_UPLOAD_FAILED;
            showToast(msg, TOAST_SEVERITY.ERROR);
            setSaving(false);
            return;
          }
        }
        showToast(GUEST_FORM_MESSAGE.BOOKING_CREATED, TOAST_SEVERITY.SUCCESS);
        setFormData(defaultBooking());
        setPendingFiles([]);
      }
      setTimeout(() => navigate(APP_ROUTES.HOME, { state: location.state }), 1500);
    } catch (err: any) {
      showToast(apiErrorToastMessage(err, isEdit ? GUEST_FORM_MESSAGE.UPDATE_FAILED : GUEST_FORM_MESSAGE.CREATE_FAILED), TOAST_SEVERITY.ERROR);
    } finally {
      setSaving(false);
    }
  }, [formData, id, isEdit, location.state, navigate, pendingFiles, showToast]);

  /** Handles form submission and opens amount confirmation when entered total differs from calculated total. */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const enteredAmount = Number(formData.TOTAL_AMOUNT) || 0;
    if (Math.abs(systemCalculatedTotal - enteredAmount) > 0.01) {
      setAmountOverrideReason(formData.AMOUNT_OVERRIDE_REASON || '');
      setAmountOverrideNote(formData.AMOUNT_OVERRIDE_NOTE || '');
      setAmountOverrideError('');
      setAmountOverrideNoteError('');
      setAmountConfirm({ systemAmount: systemCalculatedTotal, enteredAmount });
      return;
    }

    await saveBooking();
  }, [
    formData.AMOUNT_OVERRIDE_NOTE,
    formData.AMOUNT_OVERRIDE_REASON,
    formData.TOTAL_AMOUNT,
    saveBooking,
    systemCalculatedTotal,
    validate,
  ]);

  /** Confirms an intentional amount override and continues with the save operation. */
  const confirmAmountAndSave = useCallback(async () => {
    if (!amountOverrideReason) {
      setAmountOverrideError(GUEST_FORM_MESSAGE.AMOUNT_REASON_REQUIRED);
      return;
    }
    if (amountOverrideNote.length > GUEST_FORM_VALIDATION_LIMIT.NOTE_MAX_LENGTH) {
      setAmountOverrideNoteError(GUEST_FORM_MESSAGE.TEXT_TOO_LONG);
      return;
    }
    setAmountConfirm(null);
    await saveBooking({
      reason: amountOverrideReason,
      note: amountOverrideNote,
      systemAmount: amountConfirm?.systemAmount || systemCalculatedTotal,
    });
  }, [amountConfirm, amountOverrideNote, amountOverrideReason, saveBooking, systemCalculatedTotal]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress size={52} /></Box>;

  return (
    <Box sx={{ background: '#eaf4fb', minHeight: '100vh', pb: 6 }}>
      {/* Page header */}
      <Box sx={{ background: 'linear-gradient(135deg,#023e8a,#0077b6)', py: 3, px: { xs: 2, md: 6 }, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(APP_ROUTES.HOME, { state: location.state })} sx={{ color: '#fff', mb: 1 }}>{GUEST_FORM_TEXT.BUTTON_BACK}</Button>
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800 }}>
          {isEdit ? GUEST_FORM_TEXT.PAGE_TITLE_EDIT : GUEST_FORM_TEXT.PAGE_TITLE_NEW}
        </Typography>
      </Box>

      <Container maxWidth="lg">
        <Box component="form" onSubmit={handleSubmit}>
          {/* ── Guest Info ─────────────────────────────────────────────── */}
          <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark', fontWeight: 700 }}>
              {GUEST_FORM_TEXT.SECTION_GUEST_INFO}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField required fullWidth label={GUEST_FORM_FIELD_LABEL.GUEST_NAME} value={formData.GUEST_NAME}
                  inputProps={{ 'data-testid': GUEST_FORM_TEST_ID.GUEST_NAME, maxLength: GUEST_FORM_VALIDATION_LIMIT.NAME_MAX_LENGTH }}
                  onChange={e => handleTopField('GUEST_NAME', e.target.value)}
                  error={!!errors.GUEST_NAME} helperText={errors.GUEST_NAME} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField required fullWidth label={GUEST_FORM_FIELD_LABEL.PHONE} value={formData.PHONE}
                  inputProps={{ maxLength: GUEST_FORM_VALIDATION_LIMIT.PHONE_MAX_LENGTH }}
                  onChange={e => handleTopField('PHONE', e.target.value)}
                  error={!!errors.PHONE} helperText={errors.PHONE} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField fullWidth label={GUEST_FORM_FIELD_LABEL.EMAIL} type="email" value={formData.EMAIL || ''}
                  inputProps={{ maxLength: GUEST_FORM_VALIDATION_LIMIT.EMAIL_MAX_LENGTH }}
                  onChange={e => handleTopField('EMAIL', e.target.value)}
                  error={!!errors.EMAIL} helperText={errors.EMAIL} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={GUEST_FORM_FIELD_LABEL.ADDRESS} multiline rows={2} value={formData.ADDRESS || ''}
                  inputProps={{ maxLength: GUEST_FORM_VALIDATION_LIMIT.TEXT_MAX_LENGTH }}
                  error={!!errors.ADDRESS} helperText={errors.ADDRESS}
                  onChange={e => handleTopField('ADDRESS', e.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Booking Dates ──────────────────────────────────────────── */}
          <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark', fontWeight: 700 }}>
              {GUEST_FORM_TEXT.SECTION_DATES_GUESTS}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField required fullWidth label={GUEST_FORM_FIELD_LABEL.CHECKIN_DATE} type="date"
                  value={formData.CHECKIN_DATE} InputLabelProps={{ shrink: true }}
                  inputProps={{ 'data-testid': GUEST_FORM_TEST_ID.CHECKIN }}
                  onChange={e => handleTopField('CHECKIN_DATE', e.target.value)}
                  error={!!errors.CHECKIN_DATE} helperText={errors.CHECKIN_DATE} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField required fullWidth label={GUEST_FORM_FIELD_LABEL.CHECKOUT_DATE} type="date"
                  value={formData.CHECKOUT_DATE} InputLabelProps={{ shrink: true }}
                  inputProps={{ 'data-testid': GUEST_FORM_TEST_ID.CHECKOUT, min: formData.CHECKIN_DATE }}
                  onChange={e => handleTopField('CHECKOUT_DATE', e.target.value)}
                  error={!!errors.CHECKOUT_DATE} helperText={errors.CHECKOUT_DATE} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="booking-type-label">{GUEST_FORM_FIELD_LABEL.BOOKING_TYPE}</InputLabel>
                  <Select
                    labelId="booking-type-label"
                    label={GUEST_FORM_FIELD_LABEL.BOOKING_TYPE}
                    value={formData.BOOKING_TYPE || deriveBookingType(formData.CHECKIN_DATE)}
                    inputProps={{ 'data-testid': GUEST_FORM_TEST_ID.BOOKING_TYPE }}
                    onChange={e => handleTopField('BOOKING_TYPE', e.target.value)}
                  >
                    {BOOKING_TYPE_OPTIONS.map(type => (
                      <MenuItem key={type} value={type}>{BOOKING_TYPE_LABEL[type]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField required fullWidth label={GUEST_FORM_FIELD_LABEL.NO_ADULTS} type="number"
                  value={formData.NO_ADULTS} inputProps={{ min: 1 }}
                  onChange={e => handleTopField('NO_ADULTS', e.target.value === '' ? '' : (parseInt(e.target.value) || 1))}
                  error={!!errors.NO_ADULTS} helperText={errors.NO_ADULTS} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField fullWidth label={GUEST_FORM_FIELD_LABEL.NO_CHILDREN} type="number"
                  value={formData.NO_CHILDREN} inputProps={{ min: 0 }}
                  error={!!errors.NO_CHILDREN} helperText={errors.NO_CHILDREN}
                  onChange={e => handleTopField('NO_CHILDREN', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField fullWidth label={GUEST_FORM_FIELD_LABEL.NO_DRIVERS} type="number"
                  value={formData.NO_DRIVERS} inputProps={{ min: 0 }}
                  error={!!errors.NO_DRIVERS} helperText={errors.NO_DRIVERS}
                  onChange={e => handleTopField('NO_DRIVERS', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))} />
              </Grid>
            </Grid>
          </Paper>

          {/* Room allocation section */}
          {(formData.dayBookings?.length ?? 0) > 0 && (
            <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark', fontWeight: 700 }}>
                <Hotel sx={{ verticalAlign: 'middle', mr: 1 }} />
                {GUEST_FORM_TEXT.SECTION_ROOM_ALLOCATION}
              </Typography>
              {errors.dayBookings && <Alert severity="error" sx={{ mb: 2 }}>{errors.dayBookings}</Alert>}

              {formData.dayBookings!.map((day, dayIdx) => {
                const dateLabel = format(new Date(day.STAY_DATE), GUEST_FORM_DATE_FORMAT.STAY_DAY_LABEL);
                const dayAvailable = availableRooms[day.STAY_DATE] || ROOM_INVENTORY;
                const dayValidationMessages = [
                  errors[`room_${day.STAY_DATE}`],
                  errors[`custom_${day.STAY_DATE}`],
                  errors[`room_count_${day.STAY_DATE}`],
                  errors[`room_inventory_${day.STAY_DATE}`],
                  errors[`duplicate_room_${day.STAY_DATE}`],
                  errors[`amount_${day.STAY_DATE}`],
                  errors[`members_${day.STAY_DATE}`],
                ].filter((message): message is string => Boolean(message));

                return (
                  <Box key={day.STAY_DATE} sx={{ mb: 3, border: '1px solid #b0d9f0', borderRadius: 2, p: 2 }}
                    onFocus={() => fetchAvailableForDate(day.STAY_DATE)}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Chip label={dateLabel} color="primary" icon={<Hotel />} />
                      <Button size="small" startIcon={<AddCircleOutline />} onClick={() => addRoomToDay(dayIdx)}
                        sx={{ color: 'secondary.main', fontWeight: 700 }}>
                        {GUEST_FORM_TEXT.BUTTON_ADD_ROOM}
                      </Button>
                    </Box>
                    {(Number(formData.NO_DRIVERS) || 0) > 0 && (
                      <Box sx={{
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: '#ffffff',
                        border: '1px solid #d0e4f5',
                        display: 'flex',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1.5,
                        flexDirection: { xs: 'column', sm: 'row' },
                      }}>
                        <Box>
                          <Typography fontWeight={700} color="primary.dark" fontSize="0.9rem">
                            {GUEST_FORM_TEXT.DRIVER_CHARGE}
                          </Typography>
                          <Typography color="text.secondary" fontSize="0.78rem">
                            {GUEST_FORM_TEXT.DRIVER_CHARGE_DESCRIPTION_PREFIX} {Number(formData.NO_DRIVERS) || 0} {GUEST_FORM_TEXT.DRIVER_CHARGE_DESCRIPTION_SUFFIX}
                          </Typography>
                        </Box>
                        <TextField
                          size="small"
                          label={GUEST_FORM_FIELD_LABEL.DRIVER_AMOUNT_PER_DAY}
                          type="number"
                          value={day.DRIVER_AMOUNT_PER_DAY ?? ''}
                          inputProps={{ min: 0, 'data-testid': GUEST_FORM_TEST_ID.DRIVER_AMOUNT(dayIdx) }}
                          sx={{ width: { xs: '100%', sm: 220 } }}
                          onChange={e => updateDayBooking(dayIdx, 'DRIVER_AMOUNT_PER_DAY', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                        />
                      </Box>
                    )}

                    {dayValidationMessages.map(message => (
                      <Alert key={message} severity="error" sx={{ mb: 1 }}>{message}</Alert>
                    ))}

                    {day.rooms.map((room, roomIdx) => {
                      const selectedNums = room.ROOM_NUMBER ? room.ROOM_NUMBER.split(',').filter(Boolean) : [];
                      const usedInOtherRows = day.rooms
                        .filter((_, i) => i !== roomIdx)
                        .flatMap(r => r.ROOM_NUMBER ? r.ROOM_NUMBER.split(',').filter(Boolean) : []);
                      const apiAvailable = dayAvailable[room.ROOM_TYPE] || [];
                      const roomOptions = [
                        ...apiAvailable.filter(rn => !usedInOtherRows.includes(rn)),
                        ...selectedNums.filter(rn => !apiAvailable.includes(rn) && !usedInOtherRows.includes(rn)),
                      ];
                      const roomTotal = (
                        (Number(room.NO_ADULTS_IN_ROOM) || 0) * (Number(room.AMOUNT_PER_PERSON) || 0)
                      ) + (
                        (Number(room.NO_CHILDREN_IN_ROOM) || 0) * (Number(room.AMOUNT_PER_PERSON) || 0) * CHILD_RATE_FACTOR
                      );
                      return (
                      <Box key={roomIdx} sx={{ display: 'grid', gap: 1.5, mb: 1.5,
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: '2fr 2fr 1fr 1fr 1fr 1fr auto' },
                        alignItems: 'flex-start',
                        p: 1.5, borderRadius: 2, bgcolor: '#f0f7ff' }}>

                        {/* Room Type */}
                        <FormControl fullWidth size="small">
                          <InputLabel>{GUEST_FORM_FIELD_LABEL.ROOM_TYPE}</InputLabel>
                          <Select label={GUEST_FORM_FIELD_LABEL.ROOM_TYPE} value={room.ROOM_TYPE}
                            onChange={e => updateRoom(dayIdx, roomIdx, 'ROOM_TYPE', e.target.value as RoomType)}>
                            {ROOM_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                          </Select>
                        </FormControl>

                        {/* Custom type or Room number */}
                        {room.ROOM_TYPE === GUEST_FORM_ROOM.OTHER_ROOM_TYPE ? (
                          <TextField size="small" fullWidth label={GUEST_FORM_FIELD_LABEL.CUSTOM_ROOM_TYPE}
                            value={room.CUSTOM_ROOM_TYPE || ''}
                            inputProps={{ maxLength: GUEST_FORM_VALIDATION_LIMIT.TEXT_MAX_LENGTH }}
                            onChange={e => updateRoom(dayIdx, roomIdx, 'CUSTOM_ROOM_TYPE', e.target.value)}
                            error={!!errors[`custom_${day.STAY_DATE}`]}
                            helperText={errors[`custom_${day.STAY_DATE}`]} />
                        ) : (
                          <FormControl fullWidth size="small">
                            <InputLabel>{GUEST_FORM_FIELD_LABEL.ROOM_NUMBERS}</InputLabel>
                            <Select
                              label={GUEST_FORM_FIELD_LABEL.ROOM_NUMBERS}
                              multiple
                              value={selectedNums}
                              SelectDisplayProps={{ 'data-testid': GUEST_FORM_TEST_ID.ROOM_NUMBER_SELECT(dayIdx, roomIdx) } as any}
                              onChange={e => updateRoom(dayIdx, roomIdx, 'ROOM_NUMBER', (e.target.value as string[]).join(','))}
                              renderValue={(sel) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(sel as string[]).map(v => <Chip key={v} label={v} size="small" />)}
                                </Box>
                              )}
                            >
                              {roomOptions.length === 0
                                ? <MenuItem disabled>{GUEST_FORM_TEXT.NO_ROOMS_AVAILABLE}</MenuItem>
                                : roomOptions.map(rn => <MenuItem key={rn} value={rn}>{rn}</MenuItem>)
                              }
                            </Select>
                            {apiAvailable.length === 0 && (
                              <FormHelperText error>{GUEST_FORM_TEXT.NO_ROOMS_AVAILABLE}</FormHelperText>
                            )}
                          </FormControl>
                        )}

                        {/* Room number text if Other */}
                        {room.ROOM_TYPE === GUEST_FORM_ROOM.OTHER_ROOM_TYPE && (
                          <TextField size="small" fullWidth label={GUEST_FORM_FIELD_LABEL.ROOM_NUMBER}
                            value={room.ROOM_NUMBER}
                            inputProps={{ maxLength: GUEST_FORM_VALIDATION_LIMIT.TEXT_MAX_LENGTH }}
                            onChange={e => updateRoom(dayIdx, roomIdx, 'ROOM_NUMBER', e.target.value)} />
                        )}

                        <TextField size="small" fullWidth label={GUEST_FORM_FIELD_LABEL.NO_OF_ROOMS} type="number"
                          value={room.ROOM_TYPE !== GUEST_FORM_ROOM.OTHER_ROOM_TYPE ? Math.max(1, selectedNums.length) : room.NO_OF_ROOMS}
                          inputProps={{ min: 1 }}
                          InputProps={{ readOnly: room.ROOM_TYPE !== GUEST_FORM_ROOM.OTHER_ROOM_TYPE }}
                          sx={{ '& input': { bgcolor: room.ROOM_TYPE !== GUEST_FORM_ROOM.OTHER_ROOM_TYPE ? '#e8f4fd' : undefined } }}
                          onChange={room.ROOM_TYPE === GUEST_FORM_ROOM.OTHER_ROOM_TYPE ? e => updateRoom(dayIdx, roomIdx, 'NO_OF_ROOMS', e.target.value === '' ? '' : (parseInt(e.target.value) || 1)) : undefined} />

                        <TextField size="small" fullWidth label={GUEST_FORM_FIELD_LABEL.NO_ADULT_IN_ROOM} type="number"
                          value={room.NO_ADULTS_IN_ROOM}
                          inputProps={{ min: 0 }}
                          onChange={e => updateRoom(dayIdx, roomIdx, 'NO_ADULTS_IN_ROOM', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))} />

                        <TextField size="small" fullWidth label={GUEST_FORM_FIELD_LABEL.NO_CHILDREN_IN_ROOM} type="number"
                          value={room.NO_CHILDREN_IN_ROOM}
                          inputProps={{ min: 0 }}
                          helperText={`${GUEST_FORM_MESSAGE.CHILD_RATE_HELPER_PREFIX} ${Math.round(CHILD_RATE_FACTOR * 100)}%`}
                          onChange={e => updateRoom(dayIdx, roomIdx, 'NO_CHILDREN_IN_ROOM', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))} />

                        <TextField size="small" fullWidth label={GUEST_FORM_FIELD_LABEL.AMOUNT_PER_PERSON} type="number"
                          value={room.AMOUNT_PER_PERSON} inputProps={{ min: 0 }}
                          error={!!errors[`amount_${day.STAY_DATE}`]}
                          helperText={errors[`amount_${day.STAY_DATE}`] || `${GUEST_FORM_MESSAGE.ROW_TOTAL_PREFIX} ${roomTotal.toFixed(2)}`}
                          onChange={e => updateRoom(dayIdx, roomIdx, 'AMOUNT_PER_PERSON', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))} />

                        <Tooltip title={GUEST_FORM_TEXT.TOOLTIP_REMOVE_ROOM}>
                          <span>
                            <IconButton color="error" onClick={() => removeRoomFromDay(dayIdx, roomIdx)}
                              disabled={day.rooms.length === 1}>
                              <RemoveCircleOutline />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Paper>
          )}

          {/* ── Financial & Extra Info ─────────────────────────────────── */}
          <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark', fontWeight: 700 }}>
              {GUEST_FORM_TEXT.SECTION_FINANCIAL}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label={GUEST_FORM_FIELD_LABEL.ADVANCE_AMOUNT} type="number"
                  value={formData.ADVANCE_AMOUNT} inputProps={{ min: 0 }}
                  error={!!errors.ADVANCE_AMOUNT} helperText={errors.ADVANCE_AMOUNT}
                  onChange={e => handleTopField('ADVANCE_AMOUNT', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField required fullWidth label={GUEST_FORM_FIELD_LABEL.TOTAL_AMOUNT} type="number"
                  value={formData.TOTAL_AMOUNT} inputProps={{ min: 0, 'data-testid': GUEST_FORM_TEST_ID.TOTAL }}
                  onChange={e => handleTopField('TOTAL_AMOUNT', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                  error={!!errors.TOTAL_AMOUNT}
                  helperText={errors.TOTAL_AMOUNT || `${GUEST_FORM_MESSAGE.TOTAL_AMOUNT_HELPER_PREFIX} ${Math.round(CHILD_RATE_FACTOR * 100)}%.`} />
                {Math.abs(totalDifference) > 0.01 && (
                  <Box sx={{ mt: 1, fontSize: '0.8rem', color: 'warning.dark', lineHeight: 1.5 }}>
                    <Box component="span" data-testid={GUEST_FORM_TEST_ID.SYSTEM_CALCULATED_TOTAL}>{GUEST_FORM_TEXT.AMOUNT_SYSTEM}: {formatCurrency(systemCalculatedTotal)}</Box>
                    {' | '}
                    <Box component="span" data-testid={GUEST_FORM_TEST_ID.ENTERED_TOTAL}>{GUEST_FORM_TEXT.AMOUNT_ENTERED}: {formatCurrency(enteredTotal)}</Box>
                    {' | '}
                    <Box component="span" data-testid={GUEST_FORM_TEST_ID.TOTAL_DIFFERENCE}>{GUEST_FORM_TEXT.AMOUNT_DIFFERENCE}: {formatSignedCurrency(totalDifference)}</Box>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={GUEST_FORM_STAFF_OPTIONS}
                  value={formData.BOOKED_BY || ''}
                  inputValue={formData.BOOKED_BY || ''}
                  onChange={(_event, value) => handleTopField('BOOKED_BY', value || '')}
                  onInputChange={(_event, value) => handleTopField('BOOKED_BY', value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label={GUEST_FORM_FIELD_LABEL.BOOKED_BY}
                      error={!!errors.BOOKED_BY}
                      helperText={errors.BOOKED_BY}
                      inputProps={{
                        ...params.inputProps,
                        'data-testid': GUEST_FORM_TEST_ID.BOOKED_BY,
                        maxLength: GUEST_FORM_VALIDATION_LIMIT.BOOKED_BY_MAX_LENGTH,
                      }}
                    />
                  )}
                />
              </Grid>
              {isEdit && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth required error={!!errors.STATUS}>
                    <InputLabel>{GUEST_FORM_FIELD_LABEL.STATUS_REQUIRED}</InputLabel>
                    <Select label={GUEST_FORM_FIELD_LABEL.STATUS_REQUIRED} value={formData.STATUS}
                      inputProps={{ 'data-testid': GUEST_FORM_TEST_ID.STATUS }}
                      onChange={e => handleTopField('STATUS', e.target.value)}>
                      <MenuItem value="" disabled>{GUEST_FORM_TEXT.STATUS_SELECT}</MenuItem>
                      {BOOKING_STATUS_OPTIONS.map(status => (
                        <MenuItem key={status} value={status}>{BOOKING_STATUS_LABEL[status]}</MenuItem>
                      ))}
                    </Select>
                    {errors.STATUS && <FormHelperText>{errors.STATUS}</FormHelperText>}
                  </FormControl>
                </Grid>
              )}
              {isEdit && formData.STATUS === BOOKING_STATUS.CANCELLED && (
                <>
                  <Grid item xs={12}>
                    <TextField fullWidth required label={GUEST_FORM_FIELD_LABEL.CANCELLATION_REASON} multiline rows={2}
                      value={formData.CANCELLATION_REASON || ''}
                      inputProps={{ 'data-testid': GUEST_FORM_TEST_ID.CANCEL_REASON, maxLength: GUEST_FORM_VALIDATION_LIMIT.TEXT_MAX_LENGTH }}
                      error={!!errors.CANCELLATION_REASON} helperText={errors.CANCELLATION_REASON}
                      onChange={e => handleTopField('CANCELLATION_REASON', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth required error={!!errors.IS_REFUNDED}>
                      <InputLabel>{GUEST_FORM_FIELD_LABEL.IS_REFUNDED_REQUIRED}</InputLabel>
                      <Select label={GUEST_FORM_FIELD_LABEL.IS_REFUNDED_REQUIRED} value={formData.IS_REFUNDED ?? ''}
                        inputProps={{ 'data-testid': GUEST_FORM_TEST_ID.IS_REFUNDED }}
                        onChange={e => handleTopField('IS_REFUNDED', e.target.value || null)}>
                        <MenuItem value="">{GUEST_FORM_TEXT.REFUND_SELECT}</MenuItem>
                        <MenuItem value={GUEST_FORM_REFUND_STATUS.YES}>{GUEST_FORM_TEXT.YES}</MenuItem>
                        <MenuItem value={GUEST_FORM_REFUND_STATUS.NO}>{GUEST_FORM_TEXT.NO}</MenuItem>
                      </Select>
                      {errors.IS_REFUNDED && <FormHelperText>{errors.IS_REFUNDED}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  {formData.IS_REFUNDED === GUEST_FORM_REFUND_STATUS.YES && (
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField fullWidth required label={GUEST_FORM_FIELD_LABEL.REFUNDED_AMOUNT} type="number"
                        value={formData.REFUNDED_AMOUNT ?? 0} inputProps={{ min: 0 }}
                        error={!!errors.REFUNDED_AMOUNT} helperText={errors.REFUNDED_AMOUNT}
                        onChange={e => handleTopField('REFUNDED_AMOUNT', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))} />
                    </Grid>
                  )}
                </>
              )}
              <Grid item xs={12}>
                <TextField fullWidth label={GUEST_FORM_FIELD_LABEL.REMARKS} multiline rows={3} value={formData.REMARKS || ''}
                  inputProps={{ maxLength: GUEST_FORM_VALIDATION_LIMIT.TEXT_MAX_LENGTH }}
                  error={!!errors.REMARKS} helperText={errors.REMARKS}
                  onChange={e => handleTopField('REMARKS', e.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Documents ──────────────────────────────────────────────── */}
          <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark', fontWeight: 700 }}>
              {GUEST_FORM_TEXT.SECTION_DOCUMENTS}
            </Typography>

            {/* Existing docs (edit mode) */}
            {existingDocs.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography fontSize="0.82rem" color="text.secondary" sx={{ mb: 1 }}>{GUEST_FORM_TEXT.EXISTING_DOCUMENTS}</Typography>
                <List dense disablePadding>
                  {existingDocs.map(doc => (
                    <ListItem key={doc.id} disableGutters sx={{ py: 0.3 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {doc.mime_type === GUEST_FORM_FILE_UPLOAD.PDF_MIME_TYPE
                          ? <PictureAsPdf fontSize="small" color="error" />
                          : <ImageIcon fontSize="small" color="primary" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.original_name}
                        secondary={`${Math.round(doc.file_size / 1024)} KB · by ${doc.uploaded_by}`}
                        primaryTypographyProps={{ fontSize: '0.85rem' }}
                        secondaryTypographyProps={{ fontSize: '0.72rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* New files selected */}
            {pendingFiles.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography fontSize="0.82rem" color="text.secondary" sx={{ mb: 1 }}>{GUEST_FORM_TEXT.PENDING_DOCUMENTS}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {pendingFiles.map((f, i) => (
                    <Chip
                      key={i}
                      icon={f.type === GUEST_FORM_FILE_UPLOAD.PDF_MIME_TYPE ? <PictureAsPdf /> : <ImageIcon />}
                      label={`${f.name} (${Math.round(f.size / 1024)} KB)`}
                      size="small"
                      onDelete={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}
                      deleteIcon={<Close />}
                      sx={{ maxWidth: 260 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={GUEST_FORM_FILE_UPLOAD.ACCEPTED_TYPES}
              multiple
              style={{ display: 'none' }}
              onChange={e => {
                const selected = Array.from(e.target.files || []);
                setPendingFiles(prev => [...prev, ...selected]);
                e.target.value = '';
              }}
            />
            <Button
              variant="outlined"
              startIcon={<AttachFile />}
              onClick={() => fileInputRef.current?.click()}
              size="small"
            >
              {GUEST_FORM_TEXT.BUTTON_ATTACH_DOCUMENTS}
            </Button>
            <Typography fontSize="0.72rem" color="text.secondary" sx={{ mt: 1 }}>
              {GUEST_FORM_TEXT.DOCUMENT_HELPER}
            </Typography>
          </Paper>

          {/* ── Actions ─────────────────────────────────────────────────── */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => navigate(APP_ROUTES.HOME, { state: location.state })} disabled={saving}>{GUEST_FORM_TEXT.BUTTON_CANCEL}</Button>
            <Button type="submit" variant="contained" disabled={saving}
              sx={{ minWidth: 140, background: 'linear-gradient(90deg,#0077b6,#00b4d8)' }}>
              {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : isEdit ? GUEST_FORM_TEXT.BUTTON_UPDATE_BOOKING : GUEST_FORM_TEXT.BUTTON_CREATE_BOOKING}
            </Button>
          </Box>
        </Box>
      </Container>

      <Dialog open={!!amountConfirm} onClose={() => setAmountConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{GUEST_FORM_MESSAGE.CONFIRM_AMOUNT_TITLE}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {GUEST_FORM_MESSAGE.CONFIRM_AMOUNT_BODY}
          </Typography>
          <Box sx={{ bgcolor: '#f0f7ff', borderRadius: 2, p: 2 }}>
            <Typography fontSize="0.9rem">
              <strong>{GUEST_FORM_MESSAGE.CONFIRM_AMOUNT_SYSTEM}</strong> ₹ {amountConfirm?.systemAmount.toFixed(2)}
            </Typography>
            <Typography fontSize="0.9rem">
              <strong>{GUEST_FORM_MESSAGE.CONFIRM_AMOUNT_ENTERED}</strong> ₹ {amountConfirm?.enteredAmount.toFixed(2)}
            </Typography>
          </Box>
          <Typography fontSize="0.85rem" color="text.secondary" sx={{ mt: 1.5 }}>
            {GUEST_FORM_MESSAGE.CONFIRM_AMOUNT_INTENTIONAL}
          </Typography>
          <TextField
            select
            required
            fullWidth
            label={GUEST_FORM_FIELD_LABEL.ADJUSTMENT_REASON}
            value={amountOverrideReason}
            error={!!amountOverrideError}
            helperText={amountOverrideError || GUEST_FORM_MESSAGE.ADJUSTMENT_REASON_HELPER}
            sx={{ mt: 2 }}
            onChange={e => {
              setAmountOverrideReason(e.target.value);
              setAmountOverrideError('');
            }}
          >
            {AMOUNT_OVERRIDE_REASON_OPTIONS.map(reason => (
              <MenuItem key={reason} value={reason}>
                {AMOUNT_OVERRIDE_REASON_LABEL[reason]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label={GUEST_FORM_FIELD_LABEL.OPTIONAL_NOTE}
            value={amountOverrideNote}
            sx={{ mt: 2 }}
            inputProps={{ maxLength: 250 }}
            error={!!amountOverrideNoteError}
            helperText={amountOverrideNoteError || GUEST_FORM_MESSAGE.OPTIONAL_NOTE_HELPER}
            onChange={e => {
              setAmountOverrideNote(e.target.value);
              setAmountOverrideNoteError('');
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAmountConfirm(null)} disabled={saving}>{GUEST_FORM_TEXT.BUTTON_REVIEW}</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={confirmAmountAndSave}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : GUEST_FORM_TEXT.BUTTON_CONFIRM_SAVE}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuestForm;
